import { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Users, Search, ChevronRight, ChevronDown, Calendar, Mail, Award, Crown, Brain, Target, TrendingUp, BarChart3 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { api } from '../../services/api';
import type { AdminUser, QuizStats } from '../../types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function AdminUsers() {
  const { theme } = useTheme();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [allUsers, setAllUsers] = useState<AdminUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [userStats, setUserStats] = useState<QuizStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const usersData = await api.getAllUsers();
      setAllUsers(usersData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setUsers(allUsers);
      return;
    }

    const filtered = allUsers.filter(user =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setUsers(filtered);
  };

  useEffect(() => {
    handleSearch();
  }, [searchTerm, allUsers]);

  const handleUserClick = async (userId: string) => {
    if (!userId) {
      console.error('User ID is undefined');
      return;
    }

    if (expandedUser === userId) {
      setExpandedUser(null);
      setUserStats(null);
      return;
    }

    setExpandedUser(userId);
    setStatsLoading(true);

    try {
      const stats = await api.getUserStats(userId);
      setUserStats(stats);
    } catch (error) {
      console.error('Error loading user stats:', error);
      setUserStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSubscriptionLabel = (subscription: string): string => {
    switch (subscription) {
      case 'DETERMINED_MONTHLY':
        return 'Déterminé (mensuel)';
      case 'DETERMINED_ANNUALLY':
        return 'Déterminé (annuel)';
      case 'MOTIVATED_MONTHLY':
        return 'Motivé (mensuel)';
      case 'MOTIVATED_ANNUALLY':
        return 'Motivé (annuel)';
      case 'SHY_MONTHLY':
        return 'Timide (mensuel)';
      case 'SHY_ANNUALLY':
        return 'Timide (annuel)';
      default:
        return subscription;
    }
  };

  const getScoreHistoryData = (stats: QuizStats) => {
    return {
      labels: stats.score_history.map(entry =>
        new Date(entry.date).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit'
        })
      ),
      datasets: [{
        label: 'Score (%)',
        data: stats.score_history.map(entry => entry.score),
        borderColor: `rgb(251, 165, 181)`,
        backgroundColor: `rgba(251, 165, 181, 0.1)`,
        tension: 0.4,
        pointRadius: 6,
        pointHoverRadius: 8,
        fill: true,
        pointBackgroundColor: `rgb(251, 165, 181)`,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }]
    };
  };

  const getScoreHistoryOptions = () => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => `Score: ${context.parsed.y}%`
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 45,
            minRotation: 45,
            font: {
              size: 12
            }
          }
        },
        y: {
          beginAtZero: true,
          max: 100,
          ticks: {
            callback: (value: number) => `${value}%`
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.05)',
          }
        }
      },
    };
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">Utilisateurs ({users.length})</h2>

      {/* Search */}
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Rechercher un utilisateur par email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setSearchTerm('')}
            className={`px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors ${
              !searchTerm ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={!searchTerm}
          >
            Effacer
          </button>
        </div>
        {searchTerm && (
          <p className="text-sm text-gray-600 mt-2">
            {users.length} utilisateur(s) trouvé(s) pour "{searchTerm}"
          </p>
        )}
      </div>


      {loading ? (
        <div className="p-8 text-center">
          <div className={`w-8 h-8 border-4 border-${theme.primary} border-t-transparent rounded-full animate-spin mx-auto mb-4`} />
          <p className="text-gray-600">Chargement...</p>
        </div>
      ) : users.length === 0 ? (
        <p className="text-gray-500 text-center py-8">Aucun utilisateur trouvé</p>
      ) : (
        <div className="space-y-4">
          {users.map((user) => (
            <div key={user.id} className="border border-gray-200 rounded-lg">
              <button
                onClick={() => handleUserClick(user.id)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors text-left"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{user.email}</span>
                        {user.role === 'admin' && (
                          <Crown className="h-4 w-4 text-yellow-500" />
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {formatDate(user.created_at)}
                        </div>
                        <div className="flex items-center">
                          <Award className="h-4 w-4 mr-1" />
                          {user.level} ({user.experience} XP)
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.subscription 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.subscription ? getSubscriptionLabel(user.subscription) : 'Gratuit'}
                      </span>
                      {user.subscribed_until && (
                        <div className="text-xs text-gray-500 mt-1">
                          Jusqu'au {formatDate(user.subscribed_until)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="ml-4">
                  {expandedUser === user.id ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* User Progress Details */}
              {expandedUser === user.id && (
                <div className="border-t border-gray-100 px-6 py-4">
                  <div className="mb-4">
                    <h4 className="font-medium mb-2">Informations détaillées</h4>
                    <div className="text-sm">
                      <span className="font-medium">Objectif:</span> {user.objective}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-3 flex items-center">
                      <Brain className="h-4 w-4 mr-2" />
                      Statistiques QCM
                    </h4>

                    {statsLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className={`w-6 h-6 border-4 border-${theme.primary} border-t-transparent rounded-full animate-spin mr-3`} />
                        <span className="text-gray-600">Chargement des statistiques...</span>
                      </div>
                    ) : userStats ? (
                      <div className="space-y-6">
                        {/* Overview Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <BarChart3 className={`h-8 w-8 text-${theme.text}`} />
                              <span className="text-2xl font-bold">{userStats.total_qcm}</span>
                            </div>
                            <h5 className="font-medium mb-1">QCM réalisés</h5>
                            <p className="text-sm text-gray-600">Total des évaluations</p>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <Target className={`h-8 w-8 text-${theme.text}`} />
                              <span className="text-2xl font-bold">{Math.round(userStats.success_rate)}%</span>
                            </div>
                            <h5 className="font-medium mb-1">Taux de réussite</h5>
                            <p className="text-sm text-gray-600">QCM à 100%</p>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <TrendingUp className={`h-8 w-8 text-${theme.text}`} />
                              <span className="text-2xl font-bold">{Math.round(userStats.average_score)}%</span>
                            </div>
                            <h5 className="font-medium mb-1">Score moyen</h5>
                            <p className="text-sm text-gray-600">Moyenne générale</p>
                          </div>
                        </div>

                        {/* Score History Chart */}
                        {userStats.score_history && userStats.score_history.length > 0 && (
                          <div>
                            <h5 className="font-medium mb-4">Évolution des scores</h5>
                            <div className="bg-white rounded-lg p-4 h-80">
                              <Line data={getScoreHistoryData(userStats)} options={getScoreHistoryOptions()} />
                            </div>
                            <p className="text-sm text-gray-500 mt-2 text-center">
                              Progression des scores au fil du temps
                            </p>
                          </div>
                        )}

                        {/* Questions by Module and Difficulty */}
                        {userStats.success_questions_by_module && Object.keys(userStats.success_questions_by_module).length > 0 && (
                          <div>
                            <h5 className="font-medium mb-4">Questions par module et difficulté</h5>
                            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                              {Object.entries(userStats.success_questions_by_module)
                                .sort(([a], [b]) => a.localeCompare(b))
                                .map(([moduleCode, moduleStats]) => (
                                <div key={moduleCode} className="bg-gray-50 rounded-lg p-4">
                                  <h6 className={`font-semibold text-lg mb-3 text-center text-${theme.text}`}>
                                    {moduleCode}
                                  </h6>

                                  <div className={`bg-${theme.secondary} rounded-lg p-3 border border-${theme.primary}`}>
                                    <div className="space-y-3">
                                      {/* Questions faciles */}
                                      {moduleStats.facile && (
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium text-gray-700">Questions faciles réussies</span>
                                            <span className="text-sm font-bold">{moduleStats.facile.correct}/{moduleStats.facile.available}</span>
                                          </div>
                                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                              className={`h-full bg-${theme.primary} transition-all duration-300`}
                                              style={{ width: `${moduleStats.facile.available > 0 ? (moduleStats.facile.correct / moduleStats.facile.available) * 100 : 0}%` }}
                                            />
                                          </div>
                                        </div>
                                      )}

                                      {/* Questions moyennes */}
                                      {moduleStats.moyen && (
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium text-gray-700">Questions moyennes réussies</span>
                                            <span className="text-sm font-bold">{moduleStats.moyen.correct}/{moduleStats.moyen.available}</span>
                                          </div>
                                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                              className={`h-full bg-${theme.primary} transition-all duration-300`}
                                              style={{ width: `${moduleStats.moyen.available > 0 ? (moduleStats.moyen.correct / moduleStats.moyen.available) * 100 : 0}%` }}
                                            />
                                          </div>
                                        </div>
                                      )}
                                      {/* Questions difficiles */}
                                      {moduleStats.difficile && (
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-medium text-gray-700">Questions difficiles réussies</span>
                                            <span className="text-sm font-bold">{moduleStats.difficile.correct}/{moduleStats.difficile.available}</span>
                                          </div>
                                          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                              className={`h-full bg-${theme.primary} transition-all duration-300`}
                                              style={{ width: `${moduleStats.difficile.available > 0 ? (moduleStats.difficile.correct / moduleStats.difficile.available) * 100 : 0}%` }}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Aucune statistique trouvée</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}